import React, { useState, useEffect, useRef } from 'react';
import { 
  ExclamationTriangleIcon, 
  XMarkIcon, 
  CheckIcon, 
  TableCellsIcon, 
  DocumentIcon,
  CloudArrowUpIcon,
  CogIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  ServerIcon,
  UserGroupIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  HomeIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import ConfirmationModal from './ConfirmationModal';
import apiClient from '../utils/apiClient';

interface MemberData {
  vorname: string;
  name: string;
  email?: string;
  tel_nummer?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  geb_am?: string;
  gender?: string;
  eintritt?: string;
  mitglied_nr?: string;
  active?: number;
  role_ids?: number[];
}

interface MembersCSVImporterProps {
  onImportSuccess?: () => void;
  onClose?: () => void;
  availableRoles?: any[];
}

const MembersCSVImporter: React.FC<MembersCSVImporterProps> = ({ 
  onImportSuccess, 
  onClose,
  availableRoles = []
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [delimiter, setDelimiter] = useState(';');
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<number>(1); // Default: Gast
  const [overrideRole, setOverrideRole] = useState(false);
  const [columnMapping, setColumnMapping] = useState({
    vorname: '',
    name: '',
    email: '',
    tel_nummer: '',
    strasse: '',
    plz: '',
    ort: '',
    geb_am: '',
    gender: '',
    eintritt: '',
    mitglied_nr: '',
    rolle: '' // Optional: Rolle aus CSV
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [importableMembers, setImportableMembers] = useState<MemberData[]>([]);
  const [existingMembers, setExistingMembers] = useState<string[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importMessage, setImportMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });

  const roleMapping: { [key: string]: number } = {
    'gast': 1,
    'gastmitglied': 1,
    'guest': 1,
    'mitglied': 2,
    'vollmitglied': 2,
    'member': 2,
    'trainer': 3,
    'kassenprüfer': 4,
    'kassenpruefer': 4,
    'auditor': 4,
    'vorstand': 5,
    'board': 5
  };

  const genderMapping: { [key: string]: string } = {
    'm': '0',
    'männlich': '0',
    'maennlich': '0',
    'male': '0',
    'w': '1',
    'f': '1',
    'weiblich': '1',
    'female': '1',
    'd': '',
    'divers': '',
    '': ''
  };

  const convertGermanDate = (dateStr: string) => {
    if (!dateStr) return '';
    // Format: DD.MM.YYYY to YYYY-MM-DD
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // Format: D.M.YYYY to YYYY-MM-DD
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // Already in ISO format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    return '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      loadFile(selectedFile);
      setCurrentStep(2);
    } else {
      setError('Bitte wählen Sie eine CSV-Datei aus');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      setFile(droppedFile);
      loadFile(droppedFile);
      setCurrentStep(2);
    } else {
      setError('Bitte nur CSV-Dateien hochladen');
    }
  };

  const loadFile = (fileToLoad: File) => {
    setError(null);
    setSuccess(null);
    setImportableMembers([]);
    setExistingMembers([]);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        processCSV(text);
        setSuccess(`Datei "${fileToLoad.name}" erfolgreich geladen`);
      } catch (err: any) {
        setError('Fehler beim Verarbeiten der CSV-Datei: ' + err.message);
      }
    };
    reader.readAsText(fileToLoad, 'UTF-8');
  };

  const processCSV = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      setError('Die CSV-Datei enthält keine Daten');
      return;
    }

    const parsedHeaders = lines[0].split(delimiter).map(h => h.trim());
    setHeaders(parsedHeaders);

    const data = lines.slice(1, Math.min(6, lines.length)).map(line => {
      const values = line.split(delimiter);
      return values.map(v => v.trim());
    });
    setPreviewData(data);

    // Auto-detect column mappings
    const mapping = { ...columnMapping };
    parsedHeaders.forEach((header, index) => {
      const lowerHeader = header.toLowerCase();
      if (lowerHeader.includes('vorname') || lowerHeader === 'firstname') {
        mapping.vorname = index.toString();
      } else if (lowerHeader.includes('nachname') || lowerHeader === 'name' || lowerHeader === 'lastname') {
        mapping.name = index.toString();
      } else if (lowerHeader.includes('email') || lowerHeader.includes('mail')) {
        mapping.email = index.toString();
      } else if (lowerHeader.includes('telefon') || lowerHeader.includes('tel') || lowerHeader === 'phone') {
        mapping.tel_nummer = index.toString();
      } else if (lowerHeader.includes('straße') || lowerHeader.includes('strasse') || lowerHeader === 'street') {
        mapping.strasse = index.toString();
      } else if (lowerHeader.includes('plz') || lowerHeader === 'zip') {
        mapping.plz = index.toString();
      } else if (lowerHeader.includes('ort') || lowerHeader === 'city') {
        mapping.ort = index.toString();
      } else if (lowerHeader.includes('geburt') || lowerHeader.includes('birthday')) {
        mapping.geb_am = index.toString();
      } else if (lowerHeader.includes('geschlecht') || lowerHeader === 'gender') {
        mapping.gender = index.toString();
      } else if (lowerHeader.includes('eintritt') || lowerHeader.includes('entry')) {
        mapping.eintritt = index.toString();
      } else if (lowerHeader.includes('mitglied') && lowerHeader.includes('nr')) {
        mapping.mitglied_nr = index.toString();
      } else if (lowerHeader.includes('rolle') || lowerHeader === 'role') {
        mapping.rolle = index.toString();
      }
    });
    setColumnMapping(mapping);
  };

  const checkForDuplicates = async () => {
    if (!columnMapping.vorname || !columnMapping.name) {
      setError('Bitte ordnen Sie mindestens Vorname und Nachname zu');
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      // Parse all members from CSV
      const lines = file ? await file.text() : '';
      const allLines = lines.split('\n').filter(line => line.trim());
      const dataLines = allLines.slice(1);

      const members: MemberData[] = [];
      const emails: string[] = [];

      for (const line of dataLines) {
        const values = line.split(delimiter).map(v => v.trim());
        
        const member: MemberData = {
          vorname: values[parseInt(columnMapping.vorname)] || '',
          name: values[parseInt(columnMapping.name)] || '',
          active: 1
        };

        // Skip empty rows
        if (!member.vorname && !member.name) continue;

        // Optional fields
        if (columnMapping.email) {
          member.email = values[parseInt(columnMapping.email)] || '';
          if (member.email) emails.push(member.email);
        }
        if (columnMapping.tel_nummer) {
          member.tel_nummer = values[parseInt(columnMapping.tel_nummer)] || '';
        }
        if (columnMapping.strasse) {
          member.strasse = values[parseInt(columnMapping.strasse)] || '';
        }
        if (columnMapping.plz) {
          member.plz = values[parseInt(columnMapping.plz)] || '';
        }
        if (columnMapping.ort) {
          member.ort = values[parseInt(columnMapping.ort)] || '';
        }
        if (columnMapping.geb_am) {
          const dateStr = values[parseInt(columnMapping.geb_am)] || '';
          member.geb_am = convertGermanDate(dateStr);
        }
        if (columnMapping.gender) {
          const genderStr = values[parseInt(columnMapping.gender)] || '';
          member.gender = genderMapping[genderStr.toLowerCase()] || '';
        }
        if (columnMapping.eintritt) {
          const dateStr = values[parseInt(columnMapping.eintritt)] || '';
          member.eintritt = convertGermanDate(dateStr);
        }
        if (columnMapping.mitglied_nr) {
          member.mitglied_nr = values[parseInt(columnMapping.mitglied_nr)] || '';
        }

        // Handle role
        if (!overrideRole && columnMapping.rolle) {
          const roleStr = values[parseInt(columnMapping.rolle)] || '';
          const roleId = roleMapping[roleStr.toLowerCase()];
          if (roleId) {
            member.role_ids = [roleId];
          } else {
            member.role_ids = [selectedRole];
          }
        } else {
          member.role_ids = [selectedRole];
        }

        members.push(member);
      }

      // Check for existing members by email
      const existing: string[] = [];
      if (emails.length > 0) {
        try {
          const response = await apiClient.post('/api/members/check-emails', { emails });
          if (response.data.existing) {
            existing.push(...response.data.existing);
          }
        } catch (err) {
          console.error('Error checking existing members:', err);
        }
      }

      setImportableMembers(members);
      setExistingMembers(existing);
      setCurrentStep(3);
      
      setSuccess(`${members.length} Mitglieder gefunden${existing.length > 0 ? `, ${existing.length} existieren bereits` : ''}`);
    } catch (err: any) {
      setError('Fehler beim Prüfen der Daten: ' + err.message);
    } finally {
      setIsChecking(false);
    }
  };

  const handleImport = async () => {
    setIsImporting(true);
    setImportProgress(0);
    setImportMessage('Import wird vorbereitet...');

    try {
      const totalMembers = importableMembers.length;
      let imported = 0;
      let failed = 0;
      const errors: string[] = [];

      // Send all members at once to backend
      const response = await apiClient.post('/api/members/bulk-import', {
        members: importableMembers
      });

      if (response.data.success) {
        imported = response.data.imported || totalMembers;
        failed = response.data.failed || 0;
        if (response.data.errors) {
          errors.push(...response.data.errors);
        }
      }

      setImportProgress(100);

      if (failed === 0) {
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Import erfolgreich',
          message: `${imported} Mitglieder wurden erfolgreich importiert.`,
          variant: 'single',
          onConfirm: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
            if (onImportSuccess) onImportSuccess();
            handleClose();
          },
        });
      } else {
        setConfirmModal({
          isOpen: true,
          type: 'warning',
          title: 'Import teilweise erfolgreich',
          message: `${imported} Mitglieder importiert, ${failed} fehlgeschlagen.\n${errors.join('\n')}`,
          variant: 'single',
          onConfirm: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
            if (onImportSuccess) onImportSuccess();
          },
        });
      }
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Import fehlgeschlagen',
        message: err.response?.data?.message || err.message || 'Unbekannter Fehler',
        variant: 'single',
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        },
      });
    } finally {
      setIsImporting(false);
      setImportMessage('');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} />
      
      <div className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-50 
                      w-auto sm:w-full sm:max-w-6xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-xl 
                      flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="bg-primary-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserGroupIcon className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Mitglieder CSV-Import</h2>
                <p className="text-primary-100 text-sm mt-1">
                  Importieren Sie mehrere Mitglieder aus einer CSV-Datei
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
              <div className={`rounded-full p-2 ${currentStep >= 1 ? 'bg-primary-100' : 'bg-gray-100'}`}>
                <DocumentIcon className="h-5 w-5" />
              </div>
              <span className="truncate text-sm font-medium hidden sm:inline">Datei wählen</span>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-gray-400" />
            <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
              <div className={`rounded-full p-2 ${currentStep >= 2 ? 'bg-primary-100' : 'bg-gray-100'}`}>
                <CogIcon className="h-5 w-5" />
              </div>
              <span className="truncate text-sm font-medium hidden sm:inline">Spalten zuordnen</span>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-gray-400" />
            <div className={`flex items-center gap-2 ${currentStep >= 3 ? 'text-primary-600' : 'text-gray-400'}`}>
              <div className={`rounded-full p-2 ${currentStep >= 3 ? 'bg-primary-100' : 'bg-gray-100'}`}>
                <CheckCircleIcon className="h-5 w-5" />
              </div>
              <span className="truncate text-sm font-medium hidden sm:inline">Importieren</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: File Upload */}
          {currentStep === 1 && (
            <div className="max-w-3xl mx-auto">
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  isDragging 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <CloudArrowUpIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  CSV-Datei hier ablegen
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  oder
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm text-sm"
                >
                  Datei auswählen
                </button>
              </div>

              {/* CSV Format Info */}
              <div className="mt-6 bg-info-50 dark:bg-info-900/20 rounded-xl p-4 border border-info-200 dark:border-info-800">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="h-5 w-5 text-info-600 dark:text-info-400 mt-0.5" />
                  <div className="text-sm text-info-900 dark:text-info-100">
                    <p className="font-medium mb-2">CSV-Format Hinweise:</p>
                    <ul className="list-disc list-inside space-y-1 text-info-800 dark:text-info-200">
                      <li>Pflichtfelder: Vorname, Nachname</li>
                      <li>Optionale Felder: Email, Telefon, Adresse, Geburtsdatum, Geschlecht, Eintrittsdatum, Mitgliedsnummer, Rolle</li>
                      <li>Datumsformat: DD.MM.YYYY oder YYYY-MM-DD</li>
                      <li>Geschlecht: m/männlich, w/weiblich, oder leer</li>
                      <li>Rollen: gast, mitglied, trainer, kassenprüfer, vorstand</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Settings */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <CogIcon className="h-5 w-5 text-gray-500" />
                  Einstellungen
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Trennzeichen
                    </label>
                    <select
                      value={delimiter}
                      onChange={(e) => {
                        setDelimiter(e.target.value);
                        if (file) loadFile(file);
                      }}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg"
                    >
                      <option value=";">Semikolon (;)</option>
                      <option value=",">Komma (,)</option>
                      <option value="\t">Tab</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Standard-Rolle
                    </label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg"
                    >
                      {availableRoles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={overrideRole}
                        onChange={(e) => setOverrideRole(e.target.checked)}
                        className="rounded text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Rolle für alle überschreiben
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Column Mapping */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <TableCellsIcon className="h-5 w-5 text-gray-500" />
                  Spalten zuordnen
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(columnMapping).map(([field, value]) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                        {field === 'vorname' && (
                          <>
                            <UserIcon className="h-4 w-4 text-error-500" />
                            <span>Vorname *</span>
                          </>
                        )}
                        {field === 'name' && (
                          <>
                            <UserIcon className="h-4 w-4 text-gray-500" />
                            <span>Nachname *</span>
                          </>
                        )}
                        {field === 'email' && (
                          <>
                            <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                            <span>E-Mail</span>
                          </>
                        )}
                        {field === 'tel_nummer' && (
                          <>
                            <PhoneIcon className="h-4 w-4 text-gray-500" />
                            <span>Telefon</span>
                          </>
                        )}
                        {field === 'strasse' && (
                          <>
                            <HomeIcon className="h-4 w-4 text-gray-500" />
                            <span>Straße</span>
                          </>
                        )}
                        {field === 'plz' && (
                          <>
                            <MapPinIcon className="h-4 w-4 text-gray-500" />
                            <span>PLZ</span>
                          </>
                        )}
                        {field === 'ort' && (
                          <>
                            <MapPinIcon className="h-4 w-4 text-gray-500" />
                            <span>Ort</span>
                          </>
                        )}
                        {field === 'geb_am' && (
                          <>
                            <CalendarIcon className="h-4 w-4 text-gray-500" />
                            <span>Geburtsdatum</span>
                          </>
                        )}
                        {field === 'gender' && (
                          <>
                            <span className="text-primary-500">⚥</span>
                            <span>Geschlecht</span>
                          </>
                        )}
                        {field === 'eintritt' && (
                          <>
                            <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
                            <span>Eintrittsdatum</span>
                          </>
                        )}
                        {field === 'mitglied_nr' && (
                          <>
                            <IdentificationIcon className="h-4 w-4 text-gray-500" />
                            <span>Mitgliedsnummer</span>
                          </>
                        )}
                        {field === 'rolle' && (
                          <>
                            <ShieldCheckIcon className="h-4 w-4 text-gray-500" />
                            <span>Rolle</span>
                          </>
                        )}
                      </label>
                      <select
                        value={value}
                        onChange={(e) => setColumnMapping({ ...columnMapping, [field]: e.target.value })}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg"
                      >
                        <option value="">-- Nicht zugeordnet --</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index.toString()}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {previewData.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <DocumentIcon className="h-5 w-5 text-gray-500" />
                    Vorschau (erste 5 Zeilen)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          {headers.map((header, index) => (
                            <th key={index} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {previewData.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review & Import */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-success-50 dark:bg-success-900/20 rounded-xl p-4 border border-success-200 dark:border-success-800">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-success-600 dark:text-success-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-success-900 dark:text-success-100">
                      Bereit zum Import
                    </p>
                    <p className="text-sm text-success-800 dark:text-success-200 mt-1">
                      {importableMembers.length} Mitglieder werden importiert
                      {existingMembers.length > 0 && (
                        <span className="text-warning-600 dark:text-warning-400">
                          {' '}({existingMembers.length} E-Mail-Adressen existieren bereits und werden übersprungen)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Member List Preview */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <UserGroupIcon className="h-5 w-5 text-gray-500" />
                  Zu importierende Mitglieder
                </h3>
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-2">
                    {importableMembers.slice(0, 50).map((member, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <div className="flex items-center gap-3">
                          <UserIcon className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {member.vorname} {member.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {member.email || 'Keine E-Mail'}
                            </p>
                          </div>
                        </div>
                        {existingMembers.includes(member.email || '') && (
                          <span className="px-2 py-1 bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300 text-xs rounded-full">
                            Existiert bereits
                          </span>
                        )}
                      </div>
                    ))}
                    {importableMembers.length > 50 && (
                      <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-2">
                        ... und {importableMembers.length - 50} weitere
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Import Progress */}
              {isImporting && (
                <div className="bg-info-50 dark:bg-info-900/20 rounded-xl p-4 border border-info-200 dark:border-info-800">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-info-600 border-t-transparent" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-info-900 dark:text-info-100">
                        {importMessage}
                      </p>
                      <div className="mt-2 bg-info-200 dark:bg-info-800 rounded-full h-2">
                        <div 
                          className="bg-info-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${importProgress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 bg-error-50 dark:bg-error-900/20 rounded-xl p-4 border border-error-200 dark:border-error-800">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-error-600 dark:text-error-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-error-800 dark:text-error-200">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mt-4 bg-success-50 dark:bg-success-900/20 rounded-xl p-4 border border-success-200 dark:border-success-800">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-success-600 dark:text-success-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-success-800 dark:text-success-200">{success}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900 rounded-b-2xl">
          <div className="flex justify-between items-center">
            <button
              onClick={handleClose}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors shadow-sm text-sm"
            >
              Abbrechen
            </button>
            <div className="flex gap-3">
              {currentStep > 1 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors shadow-sm text-sm"
                >
                  Zurück
                </button>
              )}
              {currentStep === 2 && (
                <button
                  onClick={checkForDuplicates}
                  disabled={isChecking || !columnMapping.vorname || !columnMapping.name}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm text-sm"
                >
                  {isChecking ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Prüfe...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-5 w-5" />
                      Weiter
                    </>
                  )}
                </button>
              )}
              {currentStep === 3 && (
                <button
                  onClick={handleImport}
                  disabled={isImporting || importableMembers.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm text-sm"
                >
                  {isImporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Importiere...
                    </>
                  ) : (
                    <>
                      <ServerIcon className="h-5 w-5" />
                      Importieren ({importableMembers.filter(m => !existingMembers.includes(m.email || '')).length})
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: confirmModal.onConfirm
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: confirmModal.onConfirm
          }
        } : undefined}
      />
    </>
  );
};

export default MembersCSVImporter;